import { useCallback } from "react";
import { useImperativeModal } from "~/ImperativeModalProvider";
import { useApi } from "~/providers/ApiProvider";
import { useUserStore } from "./useUserStore";
import { ConfirmModal } from "~/components/Modal/ConfirmModal";
import { Box as MuiBox, styled } from "@mui/material";
import concurrentEditStore from "~/store/concurrentEditStore";
import Icon from "~/components/Icon";

const Box = styled(MuiBox)(
  () => `
  &.content-wrapper {
    position: relative;
    padding-left: 24px;
  }

  &.icon-wrapper {
    position: absolute;
    top: 2px;
    left: 0;
  }
`
);

type FormChartTabType =
  | "APPOINTMENT"
  | "REGISTRATION"
  | "CONSULTING"
  | "TREATMENT"
  | "SURGERY"
  | "NURSE_OPERATION"
  | "NURSE_CARE"
  | "SKINCARE"
  | "PAYMENT"
  | "PENCHART"
  | "SMS"
  | "CALL_HISTORY"
  | "PRESCRIPTIONS"
  | "SURVEY";

type DocumentType = "SESSION" | "CHART";

type CheckBeforeSavingType = <T = void>(
  documentId: number,
  tabType: FormChartTabType,
  updatedAt: string,
  callback: () => T | Promise<T>
) => Promise<{ cancelled: true } | { success: true; data: T } | undefined>;

interface UseConcurrentEditCheckParams {
  onError?: (error: string) => void;
}

export const useConcurrentEditCheck = ({
  onError,
}: UseConcurrentEditCheckParams = {}) => {
  const { userApi } = useApi();
  const imperativeModal = useImperativeModal();
  const { getConfigByCodeName } = useUserStore();

  const getDocumentType = useCallback(
    (tabType: FormChartTabType): DocumentType => {
      return ["APPOINTMENT", "REGISTRATION"].includes(tabType)
        ? "SESSION"
        : "CHART";
    },
    []
  );

  const checkAndExecute = useCallback(
    async (
      documentId: number,
      tabType: FormChartTabType,
      callback: () => void
    ) => {
      const isConcurrentEditEnabled =
        getConfigByCodeName("concurrent_edit")?.codeValue === "True";

      const documentType = getDocumentType(tabType);

      const executeCallback = () => {
        if (isConcurrentEditEnabled) {
          userApi.startEditingSign({
            documentId,
            documentType,
          });
          concurrentEditStore.setCurrentEditing({
            documentId,
            documentType,
          });
        }
        callback();
      };

      if (!isConcurrentEditEnabled) {
        return executeCallback();
      }

      try {
        // 편집 상태 확인
        const { data: checked } = await userApi.getEditingStatus({
          documentId,
          documentType,
        });

        if (checked.data.userId) {
          const confirmed = await imperativeModal.open((close) => (
            <ConfirmModal
              open={true}
              onConfirm={() => close(true)}
              onClose={() => close(false)}
              confirmText="그래도 편집"
              body={
                <Box className="content-wrapper">
                  <Box className="icon-wrapper" component={"span"}>
                    <Icon variant="reportProblem" />
                  </Box>
                  {checked.data.userName}님이 작성중입니다.
                  <br />
                  동시에 편집 시 내용이 유실될 수 있으니 주의하세요.
                </Box>
              }
            />
          ));

          if (!confirmed) return;
        }

        executeCallback();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "편집 상태 확인 중 오류가 발생했습니다.";

        if (onError) {
          onError(errorMessage);
        } else {
          console.error(errorMessage);
        }
      }
    },
    [getConfigByCodeName, getDocumentType, onError]
  );

  const checkBeforeSaving: CheckBeforeSavingType = useCallback(
    async (documentId, tabType, updatedAt, callback) => {
      const isConcurrentEditEnabled =
        getConfigByCodeName("concurrent_edit")?.codeValue === "True";
      const documentType = getDocumentType(tabType);

      if (!isConcurrentEditEnabled) {
        const result = await callback();
        return { success: true, data: result };
      }

      try {
        const { data: checked } = await userApi.checkBeforeSaving({
          documentId,
          documentType,
          updatedAt,
        });

        if (checked.data.userId) {
          const confirmed = await imperativeModal.open((close) => (
            <ConfirmModal
              open={true}
              onConfirm={() => close(true)}
              onClose={() => close(false)}
              confirmText="그래도 저장"
              body={
                <Box className="content-wrapper">
                  <Box className="icon-wrapper" component={"span"}>
                    <Icon variant="reportProblem" />
                  </Box>
                  {checked.data.userName}님이 수정한 내용이 있습니다.
                  <br />
                  저장 시 현재 작성 내용만 최종 저장됩니다. 주의하세요.
                </Box>
              }
            />
          ));

          if (!confirmed) {
            return { cancelled: true };
          }
        }

        const result = await callback();
        return { success: true, data: result };
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "저장 전 확인 중 오류가 발생했습니다.";

        if (onError) {
          onError(errorMessage);
        } else {
          console.error(errorMessage);
        }
        throw error;
      }
    },
    [getConfigByCodeName, getDocumentType, onError, userApi, imperativeModal]
  );

  const cancelEditingMode = useCallback(async () => {
    const currentEditing = concurrentEditStore.currentEditing;
    if (!currentEditing) {
      console.warn("취소할 편집 세션이 없습니다.");
      return;
    }

    const isConcurrentEditEnabled =
      getConfigByCodeName("concurrent_edit")?.codeValue === "True";

    if (isConcurrentEditEnabled) {
      concurrentEditStore.clearCurrentEditing();
      try {
        await userApi.resetEditingSign({
          documentId: currentEditing.documentId,
          documentType: currentEditing.documentType,
        });

        // 편집 모드 해제 후 저장된 정보 초기화
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "편집 모드 해제 중 오류가 발생했습니다.";

        if (onError) {
          onError(errorMessage);
        } else {
          console.error(errorMessage);
        }
      }
    }
  }, [getConfigByCodeName, onError]);

  return { checkAndExecute, checkBeforeSaving, cancelEditingMode };
};
